import classNames from "classnames";

import { ReactComponent as HumanSVG } from "@/assets/img/bmiData/human.svg";
import { ReactComponent as LinesSVG } from "@/assets/img/bmiData/lines.svg";
import { ReactComponent as TriangleSVG } from "@/assets/img/bmiData/triangle.svg";
import { RangeChart } from "@/common/RangeChart";
import StickyButton from "@/common/StickyButton";
import { logAnalyticsEvent } from "@/constants/amplitude";
import { BMI_RANGES, getBMI } from "@/constants/bmiInfo";
import { WebEventProps, WebEventTypes } from "@/constants/event";
import { FitnessGoals, goalTextById } from "@/constants/fitnessGoal";
import { FitnessLevels, getFitnessLevelLabel } from "@/constants/fitnessLevels";
import { usePrefix } from "@/hooks/usePrefix";
import i18n from "@/localization/i18next";
import tonedBody from "@/male/assets/bmi-screen/toned-body.webp";
import { QuestionComponentProps } from "@/types/common";
import { Genders } from "@/types/gender";
import useAnswers from "@/utils/useAnswers";

const BmiScreen = ({ handleNext }: QuestionComponentProps) => {
    const { getEventType } = usePrefix();
    const { answers } = useAnswers();

    const handleProceed = () => {
        logAnalyticsEvent(WebEventTypes.RESULT_EVENT, {
            [WebEventProps.SCREEN]: getEventType("bmi_results"),
        });
        logAnalyticsEvent(WebEventTypes.WEB_BUTTON_CLICK, {
            [WebEventProps.BUTTON_NAME]: "proceed",
            [WebEventProps.SCREEN]: getEventType("bmi_results"),
        });

        handleNext();
    };

    const {
        height,
        current_weight: weight,
        fitness_level: fitnessLevel,
        sedentary_lifestyle: lifestyle,
        primary_goal,
    } = answers;
    const lifestyleLabel = lifestyle === "Yes" ? i18n.t("sedentary") : i18n.t("active");
    const exerciseLabel = goalTextById[primary_goal as FitnessGoals];
    const fitnessLevelLabel = getFitnessLevelLabel(fitnessLevel as FitnessLevels, Genders.Male);

    const BMI = getBMI(weight as number, height as number);

    return (
        <div className="zing-max-w grid h-full content-between justify-center gap-10 text-white">
            <div className="grid gap-4">
                <div className="text-zing-28-26 font-semibold leading-[40px] text-white">
                    {i18n.t("your_fitness_level")}
                </div>
                <div className="grid gap-4">
                    <div className="relative flex justify-between rounded-2xl bg-zing-black-200 pr-[40%]">
                        <div className="grid gap-2 p-6">
                            <div className="flex items-center gap-2">
                                <HumanSVG />
                                <div className="grid">
                                    <div className="text-zing-12-18 text-zing-gray-150">
                                        {i18n.t("lifestyle_prompt")}
                                    </div>
                                    <div className="text-base animate-[textBlack200ToWhite_500ms_ease-in-out_forwards] font-semibold text-zing-black-200">
                                        {lifestyleLabel}
                                    </div>
                                </div>
                            </div>
                            <div className="h-[1px] bg-zing-gray-400"></div>
                            <div className="flex items-center gap-2">
                                <TriangleSVG />
                                <div className="grid">
                                    <div className="text-zing-12-18 text-zing-gray-150">
                                        {i18n.t("exercise_prompt")}
                                    </div>
                                    <div className="text-base animate-[textBlack200ToWhite_500ms_ease-in-out_500ms_forwards] font-semibold text-zing-black-200">
                                        {exerciseLabel}
                                    </div>
                                </div>
                            </div>
                            <div className="h-[1px] bg-zing-gray-400"></div>
                            <div className="flex items-center gap-2">
                                <LinesSVG />
                                <div className="grid">
                                    <div className="text-zing-12-18 text-zing-gray-150">
                                        {i18n.t("activity_level")}
                                    </div>
                                    <div className="text-base animate-[textBlack200ToWhite_500ms_ease-in-out_1000ms_forwards] font-semibold capitalize text-zing-black-200">
                                        {fitnessLevelLabel}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <img src={tonedBody} alt="" className="absolute right-0 max-h-full" />
                    </div>
                    <div className="grid animate-[appear_150ms_cubic-bezier(0.4,_0,_0.2,_1)_1500ms_forwards] flex-col gap-6 rounded-2xl bg-zing-black-200 p-6 opacity-0">
                        <div className="flex items-center justify-between text-zing-16-22 font-semibold">
                            <div>{i18n.t("bmi_message")}</div>
                            <div className="whitespace-nowrap rounded-2xl bg-zing-gray-400 px-4 py-1 text-zing-12-18 font-normal capitalize">
                                {i18n.t("normal_bmi")}
                            </div>
                        </div>
                        <RangeChart
                            value={BMI}
                            ranges={BMI_RANGES}
                            animationDelay={2000}
                            colorTheme="red"
                        />
                        <div className="text-zing-12-18">{i18n.t("ideal_men_bmi_description")}</div>
                    </div>
                </div>
            </div>
            <StickyButton
                onClick={handleProceed}
                className={classNames(
                    "bottom-0 animate-[appear_1000ms_cubic-bezier(0.4,_0,_0.2,_1)_3500ms_forwards] opacity-0"
                )}
            />
        </div>
    );
};

export default BmiScreen;
